import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  SelectChangeEvent,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Paper,
  Alert,
  Button,
} from "@mui/material";
import { LuxorHeader } from "./components/LuxorHeader";
import { endpointConfigs } from "./luxorMedSettings";
import { SessionHistory, ResponseHistoryItem } from "./types";
import { SessionSelector } from "./components/SessionSelector";
import { TextEditor } from "./components/TextEditor";
import { ResponseRenderer } from "./components/ResponseRenderer";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import {
  useFilteredConditions,
  useFilteredAllergies,
  useFilteredImmunizations,
  useFilteredSelectedInterventions,
} from "@/pages/medicalcare/components/RegisterArea/useFilteredMedicalData";
import { useFormatter } from "@/utils/useFormatter";
import { ResponseHistory } from "./components/ResponseHistory";
import { WarningNoContract } from "./components/WarningNoContract";
import { Actions } from "./components/Actions";
import { isTypeAvailable } from "./isTypeAvailable";
import { useFhirData } from "@/utils/useFhirData";
import {
  initializeLuxorMedSession,
  LuxorMedResponse
} from "./utils/manageHistoryLuxorMed";

const getResponseTypeFromSelectedType = (selectedType: string): 'luxor-medical-report' | 'luxor-patient-case-analysis' | 'luxor-patient-guidance' => {
  switch (selectedType) {
    case 'medical-reports':
      return 'luxor-medical-report';
    case 'discussion-case':
      return 'luxor-patient-case-analysis';
    case 'guidance':
      return 'luxor-patient-guidance';
    default:
      return 'luxor-medical-report';
  }
};

export default function LuxorMed() {
  const {
    luxorCurrentSession: selectedType,
    setLuxorCurrentSession: setSelectedType,
    luxorSessionsHistory: sessionsHistory,
    setLuxorSessionsHistory: setSessionsHistory,
    luxorResponseHistory: responseHistory,
    setLuxorResponseHistory: setResponseHistory,
    luxorShowEditor: showEditor,
    setLuxorShowEditor: setShowEditor,
    luxorAppointmentHistory,
    addLuxorResponse,
  } = useMedicalCareContext();
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCurrentSessionHistory, setShowCurrentSessionHistory] = useState(false);
  const [luxorSessionId, setLuxorSessionId] = useState<string | null>(null);

  const { copyMedicalData, appointment, patient, condutas, problems } =
    useMedicalCareContext();
  const { contractDiscussionCase } = useDiscussionCaseContext();
  const { contractMedicalReports } = useMedicalReportsAgentContext();
  const { contractGuidance } = useGuidanceAgentContext();
  const { organizationId } = useOrganizationContext();

  const hasAnyAIContract = useMemo(() => {
    return !!(
      contractDiscussionCase ||
      contractMedicalReports ||
      contractGuidance
    );
  }, [contractDiscussionCase, contractMedicalReports, contractGuidance]);

  const selectedTypeHasContract = isTypeAvailable(
    selectedType,
    contractDiscussionCase,
    contractMedicalReports,
    contractGuidance
  );

  const currentSession = sessionsHistory[selectedType] || {
    content: "",
    response: "",
    showEditor: true,
    appointmentId: appointment?.id,
    timestamp: Date.now(),
  };

  const content = currentSession.content;
  const response = currentSession.response;

  const activeSessionTypes = Object.keys(sessionsHistory);

  const currentAppointmentResponses = useMemo(() => {
    return luxorAppointmentHistory.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [luxorAppointmentHistory]);

  useEffect(() => {
    const savedSessions = localStorage.getItem("luxorSessionsHistory");
    if (savedSessions) {
      try {
        setSessionsHistory(JSON.parse(savedSessions));
      } catch { }
    }
    const savedResponses = localStorage.getItem("luxorResponseHistory");
    if (savedResponses) {
      try {
        setResponseHistory(JSON.parse(savedResponses));
      } catch { }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "luxorSessionsHistory",
      JSON.stringify(sessionsHistory)
    );
  }, [sessionsHistory]);

  useEffect(() => {
    localStorage.setItem(
      "luxorResponseHistory",
      JSON.stringify(responseHistory)
    );
  }, [responseHistory]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => setCopied(false), 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [copied]);

  useEffect(() => {
    if (copyMedicalData && copyMedicalData.length > 0) {
      setSessionsHistory((prev) => {
        const latestSession = prev[selectedType] || {
          content: "",
          response: "",
          showEditor: true,
          appointmentId: appointment?.id,
          timestamp: Date.now(),
        };

        return {
          ...prev,
          [selectedType]: {
            ...latestSession,
            content: copyMedicalData,
            showEditor: true,
            appointmentId: appointment?.id,
            timestamp: Date.now(),
          },
        };
      });

      setShowEditor(true);
    }
  }, [copyMedicalData, appointment?.id, selectedType]);

  const handleTypeChange = (event: SelectChangeEvent) => {
    setSelectedType(event.target.value);
  };



  const setContent = (newContent: string) => {
    setSessionsHistory((prev) => ({
      ...prev,
      [selectedType]: {
        ...currentSession,
        content: newContent,
      },
    }));
  };

  const handleCopyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(response);
      setCopied(true);
    }
  };

  const handleToggleEditor = () => {
    setShowEditor((prev) => {
      const newState = !prev;
      localStorage.setItem('luxorEditorState', JSON.stringify(newState));
      return newState;
    });
  };

  useEffect(() => {
    const savedEditorState = localStorage.getItem("luxorEditorState");
    if (savedEditorState !== null) {
      setShowEditor(JSON.parse(savedEditorState));
    }
  }, []);

  useEffect(() => {
    if (appointment?.id && patient?.id) {
      const practitionerParticipant = useFhirData.findParticipant(
        appointment?.participant,
        "Practitioner"
      );
      const practitionerId = practitionerParticipant?.reference?.split("/")?.[1];

      const sessionId = initializeLuxorMedSession(patient.id, practitionerId);
      setLuxorSessionId(sessionId);
    }
  }, [appointment?.id, patient?.id]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    try {
      setIsLoading(true);
      setSessionsHistory((prev) => ({
        ...prev,
        [selectedType]: {
          ...currentSession,
          response: "",
        },
      }));
      const config = endpointConfigs[selectedType];
      let endpoint = `${import.meta.env.VITE_ENDPOINT_N8N}${config.endpoint}`;
      const requestBody = await config.bodyBuilder(content, organizationId);

      const response = await useAxios.post(endpoint, requestBody, {
        headers: {
          Authorization: await useAuth.getUserToken(),
        },
        timeout: 120000,
      });

      let responseText = "";
      if (response && response.data) {
        console.log("Processing response data:", response.data);
        if (response.data.output) {
          responseText = response.data.output;
        } else if (response.data.response) {
          responseText = response.data.response;
        } else if (response.data.text) {
          responseText = response.data.text;
        } else if (typeof response.data === "string") {
          responseText = response.data;
        } else {
          responseText = JSON.stringify(response.data);
        }
        console.log("Final response text:", responseText);
      } else {
        console.warn("No response data received");
      }

      if (!responseText) {
        throw new Error("No response text generated from API response");
      }

      const newResponseItem: ResponseHistoryItem = {
        appointmentId: appointment?.id || "desconhecido",
        type: selectedType,
        response: responseText,
        timestamp: Date.now(),
        title: endpointConfigs[selectedType].name,
      };

      setResponseHistory((prev) => [...prev, newResponseItem]);

      const luxorResponse: LuxorMedResponse = {
        responseType: getResponseTypeFromSelectedType(selectedType),
        responseContent: responseText,
        promptContent: content,
        timestamp: new Date().toISOString(),
        appointmentId: appointment?.id,
        promptVersion: "1.0.0",
        agentProvider: "LuxorMed"
      };

      addLuxorResponse(luxorResponse);

      setSessionsHistory((prev) => {
        const newSessionsHistory = {
          ...prev,
          [selectedType]: {
            ...currentSession,
            response: responseText,
            appointmentId: appointment?.id,
            timestamp: Date.now(),
          },
        };
        return newSessionsHistory;
      });
      setShowEditor(false);
    } catch (error: any) {
      setSessionsHistory((prev) => ({
        ...prev,
        [selectedType]: {
          ...currentSession,
          response:
            "Desculpe, tive um problema ao processar sua mensagem. Por favor, tente novamente.",
        },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCurrentSessionHistory = () => {
    setShowCurrentSessionHistory(!showCurrentSessionHistory);
  };

  const handleCopyCurrentResponse = (responseContent: string) => {
    navigator.clipboard.writeText(responseContent);
    setCopied(true);
  };

  const handleLoadResponse = (response: LuxorMedResponse) => {
    setSessionsHistory((prev) => ({
      ...prev,
      [selectedType]: {
        ...currentSession,
        content: response.promptContent,
        response: response.responseContent,
        showEditor: true,
        appointmentId: appointment?.id,
        timestamp: Date.now(),
      },
    }));

    setShowEditor(true);
  };

  return (
    <Box>
      <LuxorHeader
        selectedType={selectedType}
        endpointConfigs={endpointConfigs}
        onTypeChange={handleTypeChange}
        onDelete={() => { }}
        onClose={() => { }}
        onToggleEditor={handleToggleEditor}
        showEditor={showEditor}
        hasResponse={!!response}
        hasHistory={responseHistory.length > 0}
        content={content}
        onCopyToEditor={() => {
          setShowEditor(true);
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          width: "100%",
        }}
      >
        {/* {currentAppointmentResponses.length > 0 && (
          <Actions
            showCurrentSessionHistory={showCurrentSessionHistory}
            toggleCurrentSessionHistory={toggleCurrentSessionHistory}
            hasCurrentSessionHistory={currentAppointmentResponses.length > 0}
          />
        )} */}
      </Box>

      {/* Histórico do atendimento atual */}
      {/* {showCurrentSessionHistory && currentAppointmentResponses.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <CurrentSessionHistory
            responses={currentAppointmentResponses}
            onCopyResponse={handleCopyCurrentResponse}
            onLoadResponse={handleLoadResponse}
          />
        </Box>
      )} */}

      {/* {showResponseHistory && responseHistory.length > 0 && (
        <ResponseHistory
          responseHistory={responseHistory}
          deleteHistoryItem={deleteHistoryItem}
        />
      )} */}

      {hasAnyAIContract && selectedTypeHasContract ? (
        <>
          <TextEditor
            content={content}
            setContent={setContent}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            showFull={showEditor}
          />

          <ResponseRenderer
            response={response}
            copied={copied}
            onCopy={handleCopyResponse}
            showEditor={showEditor}
            appointmentId={currentSession.appointmentId}
          />
        </>
      ) : !selectedTypeHasContract ? (
        <WarningNoContract />
      ) : (
        <WarningNoContract />
      )}
    </Box>
  );
}
